import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Sun, Moon, Users, Wrench, ClipboardList, ShieldAlert, PenTool, ArrowRight, Circle, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import ProgressRing from "@/components/ProgressRing";

/**
 * ShiftCrewCard — one column of the Superintendent Command Centre. Renders a
 * single shift (Day or Night) with the active swing, live progress ring,
 * inspection roster, and roll-up counters.
 *
 * Props:
 *   shift        — "day" | "night"
 *   date         — YYYY-MM-DD
 *   swing        — active swing object (or null when no crew is on shift)
 *   summary      — /shift-summary response for the same date+shift
 *   onOpenInsp   — (inspectionId) => void, opens the inspection drawer
 *   loading      — bool
 */
export default function ShiftCrewCard({ shift, date, swing, summary, onOpenInsp, loading, customJobs = [] }) {
  const isDay = shift === "day";
  const accent = isDay ? "#f5b942" : "#7aa2ff";
  const trackTint = isDay ? "rgba(245,185,66,0.08)" : "rgba(122,162,255,0.08)";
  const Icon = isDay ? Sun : Moon;

  // Progress numbers from the swing (server-authoritative). Fall back to
  // shift-summary counts when no active swing (shift hasn't started yet).
  const progress = useMemo(() => {
    const p = swing?.progress || {};
    const total = Number(p.total || 0);
    const approved = Number(p.approved || 0);
    const inProgress = Number(p.in_progress || 0);
    const notStarted = Number(p.not_started || 0);
    const ready = Number(p.ready || 0);
    const sentBack = Number(p.sent_back || 0);
    const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
    return { total, approved, inProgress, notStarted, ready, sentBack, pct };
  }, [swing]);

  const counts = useMemo(() => ({
    inspections: (summary?.inspections || []).length,
    ppfs: (summary?.ppfs || []).length,
    breakdowns: (summary?.breakdowns || []).length,
    hazards: (summary?.hazards || []).length,
    handovers: (summary?.handovers || []).length,
    custom_jobs: (summary?.custom_jobs || []).length,
  }), [summary]);

  const noSwing = !swing && !loading;
  const inspectionIds = swing?.inspection_ids || [];

  return (
    <div
      className="panel-flat p-0 relative overflow-hidden"
      data-testid={`shift-crew-card-${shift}`}
      style={{
        border: `1px solid ${isDay ? "rgba(245,185,66,0.28)" : "rgba(122,162,255,0.28)"}`,
        borderTop: `3px solid ${accent}`,
      }}
    >
      {/* Ambient tint — matches the shift's mood colour */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 20% 0%, ${trackTint} 0%, transparent 55%)`,
        }}
      />

      {/* Header */}
      <div className="relative px-5 py-4 flex items-start justify-between gap-4"
           style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${accent}22 0%, ${accent}0a 100%)`,
              border: `1px solid ${accent}55`,
            }}
          >
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-black text-xl leading-none uppercase tracking-wider" style={{ color: accent }}>
              {isDay ? "Day Shift" : "Night Shift"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted mt-1 font-bold">
              {date}
            </div>
          </div>
        </div>

        {swing && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Status</div>
            <div className="mt-0.5">
              <span
                className="pill text-[10px] font-black"
                style={{
                  background: swing.status === "active" ? "rgba(74,222,128,0.14)" : "rgba(148,163,184,0.14)",
                  color: swing.status === "active" ? "#4ade80" : "#94a3b8",
                  border: `1px solid ${swing.status === "active" ? "rgba(74,222,128,0.4)" : "rgba(148,163,184,0.35)"}`,
                }}
                data-testid={`shift-status-${shift}`}
              >
                {swing.status === "active" ? "● LIVE" : String(swing.status || "").toUpperCase()}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Crew band */}
      {swing ? (
        <div className="relative px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <MetaTile icon={Users} label="Crew" value={swing.crew_name || "—"} accent={accent} testid={`crew-${shift}`} />
          <MetaTile icon={PenTool} label="Supervisor" value={swing.supervisor_name || "—"} accent={accent} testid={`supervisor-${shift}`} />
          <MetaTile icon={Wrench} label="Work Area" value={cap(swing.work_area) || "—"} accent={accent} testid={`work-area-${shift}`} />
        </div>
      ) : (
        <div className="relative px-5 py-8 text-center">
          <div
            className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3"
            style={{ background: "rgba(148,163,184,0.08)", border: "1px dashed rgba(148,163,184,0.35)" }}
          >
            <Circle className="w-5 h-5 text-muted" />
          </div>
          <div className="font-display font-black text-sm uppercase tracking-widest text-muted">
            {loading ? "Loading..." : "No Crew On Shift"}
          </div>
          <div className="text-xs text-muted mt-1">
            {loading ? "Fetching swing data…" : "Nothing scheduled for this shift yet."}
          </div>
        </div>
      )}

      {/* Live progress ring + KPIs */}
      {swing && (
        <div className="relative px-5 py-4 grid grid-cols-[auto_1fr] gap-5 items-center"
             style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <ProgressRing
            size={130}
            stroke={10}
            value={progress.pct}
            color={accent}
            label="Approved"
            ariaLabel={`${progress.approved} of ${progress.total} inspections approved`}
          >
            <div className="text-center">
              <div className="font-display font-black tabular-nums leading-none" style={{ fontSize: 34, color: accent }}>
                {progress.approved}
                <span className="text-muted text-xl">/{progress.total}</span>
              </div>
              <div className="text-[9px] uppercase tracking-widest text-muted mt-1.5 font-bold">
                Approved
              </div>
            </div>
          </ProgressRing>
          <div className="grid grid-cols-2 gap-2">
            <KpiChip label="In progress" value={progress.inProgress} icon={Clock} tone="amber" testid={`kpi-inprogress-${shift}`} />
            <KpiChip label="Ready" value={progress.ready} icon={CheckCircle2} tone="cyan" testid={`kpi-ready-${shift}`} />
            <KpiChip label="Not started" value={progress.notStarted} icon={Circle} tone="muted" testid={`kpi-notstarted-${shift}`} />
            <KpiChip label="Sent back" value={progress.sentBack} icon={AlertTriangle} tone="red" testid={`kpi-sentback-${shift}`} />
          </div>
        </div>
      )}

      {/* Rollup chips */}
      <div className="relative px-5 py-4 grid grid-cols-3 sm:grid-cols-6 gap-2"
           style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <RollupChip label="Inspections" value={counts.inspections} icon={ClipboardList} testid={`rollup-inspections-${shift}`} />
        <RollupChip label="Planning Requests" value={counts.ppfs} icon={PenTool} testid={`rollup-ppfs-${shift}`} />
        <RollupChip label="Breakdowns" value={counts.breakdowns} icon={Wrench} testid={`rollup-breakdowns-${shift}`} />
        <RollupChip label="Hazards" value={counts.hazards} icon={ShieldAlert} testid={`rollup-hazards-${shift}`} />
        <RollupChip label="Handovers" value={counts.handovers} icon={Users} testid={`rollup-handovers-${shift}`} />
        <RollupChip label="Custom" value={counts.custom_jobs} icon={ArrowRight} testid={`rollup-custom-${shift}`} />
      </div>

      {/* Inspection roster — click any row to open the drawer. Rich mode
          when the caller supplies `swing.__inspectionRows` (Command Centre
          does this): every inspection shows its live progress bar and the
          technician who started it, matching the Supervisor's SWING view.
          Falls back to a compact id-only list for legacy callers. */}
      {swing && (swing.__inspectionRows?.length > 0 || inspectionIds.length > 0) && (
        <div className="relative px-5 py-4"
             style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: accent }}>
              Live inspections on this swing
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">
              {(swing.__inspectionRows?.length || inspectionIds.length)} total
            </div>
          </div>
          <div className="space-y-1.5" data-testid={`inspection-roster-${shift}`}>
            {swing.__inspectionRows?.length > 0
              ? swing.__inspectionRows.map((r, i) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onOpenInsp?.(r.id)}
                    data-testid={`shift-insp-row-${shift}-${i}`}
                    className="w-full text-left px-3 py-2 rounded-lg hover:translate-x-[2px] transition-transform"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                  >
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-black text-sm truncate">
                          <span className="font-mono-tight" style={{ color: accent }}>{r.asset_id}</span>
                          <span className="text-white/40 mx-1.5">·</span>
                          {r.template_name}
                        </div>
                        <div className="text-[10px] text-muted truncate mt-0.5">
                          <span className="uppercase tracking-widest">{r.technician}</span>
                          <span className="mx-1.5">·</span>
                          <span className="tabular-nums">{r.done}/{r.total}</span>
                          <span className="mx-1.5">·</span>
                          <span className="uppercase">{r.status}</span>
                        </div>
                      </div>
                      <div className="shrink-0 tabular-nums font-display font-black text-sm" style={{ color: accent, minWidth: 42 }}>
                        {r.pct}%
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${r.pct}%`, background: accent }}
                        data-testid={`shift-insp-progress-${shift}-${i}`}
                      />
                    </div>
                  </button>
                ))
              : inspectionIds.slice(0, 12).map((iid, i) => (
                  <button
                    key={iid}
                    type="button"
                    onClick={() => onOpenInsp?.(iid)}
                    data-testid={`shift-insp-row-${shift}-${i}`}
                    className="text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium hover:translate-x-[2px] transition-transform w-full"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                  >
                    <ClipboardList className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
                    <span className="truncate">#{iid.slice(0, 8)}</span>
                    <ArrowRight className="w-3 h-3 ml-auto opacity-60 shrink-0" />
                  </button>
                ))}
          </div>
        </div>
      )}

      {/* Custom Jobs — asset-demands lodged / signed off during this shift.
          Same live signal the Supervisor sees on their SWING page. */}
      {customJobs.length > 0 && (
        <div className="relative px-5 py-4"
             style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: accent }}>
              Custom Jobs ({customJobs.length})
            </div>
          </div>
          <div className="space-y-1.5" data-testid={`custom-jobs-${shift}`}>
            {customJobs.slice(0, 8).map((cj, i) => (
              <div
                key={cj.id || i}
                className="px-3 py-2 rounded-lg text-xs"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`custom-job-${shift}-${i}`}
              >
                <div className="font-display font-black text-sm truncate">
                  <span className="font-mono-tight" style={{ color: accent }}>{cj.asset_id || "—"}</span>
                  <span className="text-white/40 mx-1.5">·</span>
                  {cj.text || cj.title || cj.description || "Custom job"}
                </div>
                <div className="text-[10px] text-muted truncate mt-0.5">
                  {(cj.completed_by_name || cj.created_by_name || cj.signed_by_name || "—")}
                  {cj.status && <> · <span className="uppercase">{cj.status}</span></>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer — deep link into full shift summary for the drilldown */}
      <div className="relative px-5 py-3 flex items-center justify-between"
           style={{ borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.15)" }}>
        <Link
          to={`/shift-summary?date=${date}&shift=${shift}`}
          className="text-[10px] uppercase tracking-widest font-black hover:opacity-80 transition-opacity"
          style={{ color: accent }}
          data-testid={`shift-summary-link-${shift}`}
        >
          Open Full Shift Summary →
        </Link>
        {noSwing && (
          <Link
            to={`/swing?shift=${shift}&date=${date}`}
            className="text-[10px] uppercase tracking-widest font-black text-muted hover:opacity-80"
            data-testid={`start-swing-link-${shift}`}
          >
            Start Swing
          </Link>
        )}
      </div>
    </div>
  );
}

function MetaTile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-muted font-bold mb-1">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        {label}
      </div>
      <div className="font-display font-black text-sm truncate" title={value}>
        {value || "—"}
      </div>
    </div>
  );
}

function KpiChip({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    amber: { c: "#fbbf24", bg: "rgba(251,191,36,0.10)" },
    cyan: { c: "#22d3ee", bg: "rgba(34,211,238,0.10)" },
    muted: { c: "#94a3b8", bg: "rgba(148,163,184,0.10)" },
    red: { c: "#f87171", bg: "rgba(248,113,113,0.10)" },
  };
  const t = tones[tone] || tones.muted;
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-2 px-3 py-2 rounded-lg"
      style={{ background: t.bg, border: `1px solid ${t.c}22` }}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: t.c }} />
      <div className="min-w-0">
        <div className="font-display font-black text-lg leading-none tabular-nums" style={{ color: t.c }}>
          {value}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-muted font-bold mt-0.5 truncate">
          {label}
        </div>
      </div>
    </div>
  );
}

function RollupChip({ label, value, icon: Icon, testid }) {
  const hot = value > 0;
  return (
    <div
      data-testid={testid}
      className="text-center rounded-lg py-2 px-1"
      style={{
        background: hot ? "rgba(245,130,32,0.06)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${hot ? "rgba(245,130,32,0.25)" : "rgba(255,255,255,0.05)"}`,
      }}
    >
      <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-widest text-muted font-bold mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div
        className="font-display font-black text-xl tabular-nums leading-none"
        style={{ color: hot ? "var(--primary)" : "rgba(255,255,255,0.35)" }}
      >
        {value}
      </div>
    </div>
  );
}

function cap(s) {
  if (!s) return "";
  const str = String(s);
  return str.charAt(0).toUpperCase() + str.slice(1);
}
